import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, CircularProgress, Button, TextField, Chip,
  Collapse, IconButton, FormControl, InputLabel, Select, MenuItem,
  OutlinedInput, Checkbox, ListItemText, SelectChangeEvent, InputAdornment,
  Autocomplete
} from '@mui/material';
import {
  Search as SearchIcon, Print as PrintIcon,
  ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon,
  Refresh as RefreshIcon, LocalShipping as ShippingIcon, Clear as ClearIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import axiosInstance from '../services/axiosInstance';
import { useLanguage } from '../contexts/LanguageContext';
import { useAppSelector } from '../hooks/redux.hooks';
import { useFiscalDateRange } from '../hooks/useFiscalDateRange';

// ============================================
// Interfaces
// ============================================
interface BranchQuantity {
  branchId: number; branchName: string; branchNameAr: string;
  purchaseOrderId: number; poNumber: string;
  quantity: number; unitPrice: number; totalPrice: number;
}
interface ConsolidatedItem {
  itemId: number; itemName: string; itemNameAr: string; itemCode: string;
  categoryId: number; categoryName: string; categoryNameAr: string;
  unitId: number; unitName: string; unitNameAr: string;
  totalQuantity: number; averageUnitPrice: number; totalAmount: number;
  branchDetails: BranchQuantity[];
}
interface ConsolidationResult {
  fromDate: string; toDate: string;
  totalOrders: number; totalBranches: number; totalItems: number; grandTotal: number;
  supplierName: string | null; custodyName: string | null;
  items: ConsolidatedItem[];
}
interface Branch { branchId: number; branchName: string; branchNameAr: string; }
interface Supplier { supplierId: number; supplierCode: string; supplierName: string; supplierNameAr: string; }
interface Custody { custodyId: number; custodyNumber: string; employeeName: string; employeeNameAr: string; status: string; }
interface Category { categoryId: number; categoryName: string; categoryNameAr: string; }

// ============================================
// مكون بحث داخل القائمة المنسدلة
// ============================================
const DropdownSearch: React.FC<{ value: string; onChange: (v: string) => void; placeholder: string }> = ({ value, onChange, placeholder }) => (
  <Box sx={{ px: 1, py: 0.5, position: 'sticky', top: 0, zIndex: 1, backgroundColor: 'background.paper' }}>
    <TextField size="small" fullWidth placeholder={placeholder} value={value}
      onChange={e => onChange(e.target.value)} autoFocus
      onKeyDown={e => e.stopPropagation()}
      InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
  </Box>
);

// ============================================
// Translations
// ============================================
const translations = {
  en: {
    title: 'Purchase Orders Consolidation', subtitle: 'Consolidate purchase orders from all branches',
    fromDate: 'From Date', toDate: 'To Date', branches: 'Branches', allBranches: 'All Branches',
    supplier: 'Supplier', allSuppliers: 'All Suppliers', custody: 'Custody', allCustodies: 'All Custodies',
    category: 'Category', allCategories: 'All Categories', searchItem: 'Search item...',
    search: 'Search', refresh: 'Refresh', print: 'Print', printDetailed: 'Print Detailed',
    selectAll: 'Select All', clear: 'Clear Filters', searchInList: 'Search...',
    item: 'Item', itemCode: 'Code', unit: 'Unit', totalQty: 'Total Qty',
    avgPrice: 'Avg Price', totalAmount: 'Total Amount', branch: 'Branch', poNumber: 'PO#',
    quantity: 'Quantity', unitPrice: 'Unit Price', total: 'Total',
    noData: 'No purchase orders found', totalOrders: 'Total Orders',
    totalBranches: 'Total Branches', totalItems: 'Total Items', grandTotal: 'Grand Total',
    kwd: 'KWD', noAccess: 'You do not have access to this page',
  },
  ar: {
    title: 'تجميع أوامر الشراء', subtitle: 'تجميع أوامر الشراء من كل الفروع',
    fromDate: 'من تاريخ', toDate: 'إلى تاريخ', branches: 'الفروع', allBranches: 'كل الفروع',
    supplier: 'المورد', allSuppliers: 'كل الموردين', custody: 'العهدة', allCustodies: 'كل العهد',
    category: 'الفئة', allCategories: 'كل الفئات', searchItem: 'بحث عن صنف...',
    search: 'بحث', refresh: 'تحديث', print: 'طباعة', printDetailed: 'طباعة تفصيلي',
    selectAll: 'اختيار الكل', clear: 'مسح الفلاتر', searchInList: 'بحث...',
    item: 'الصنف', itemCode: 'الكود', unit: 'الوحدة', totalQty: 'الكمية الإجمالية',
    avgPrice: 'متوسط السعر', totalAmount: 'المبلغ الإجمالي', branch: 'الفرع', poNumber: 'رقم الطلب',
    quantity: 'الكمية', unitPrice: 'سعر الوحدة', total: 'الإجمالي',
    noData: 'لا توجد أوامر شراء', totalOrders: 'عدد الطلبات',
    totalBranches: 'عدد الفروع', totalItems: 'عدد الأصناف', grandTotal: 'الإجمالي العام',
    kwd: 'د.ك', noAccess: 'ليس لديك صلاحية لهذه الصفحة',
  }
};

const PurchaseConsolidationPage: React.FC = () => {
  const { language } = useLanguage();
  const t = translations[language as keyof typeof translations] || translations.en;
  const isRtl = language === 'ar';
  const { minDate, maxDate } = useFiscalDateRange();
  const { user } = useAppSelector((state) => state.auth);

  const isBranchManager = user && !user.isSuperAdmin && user.roles?.some((r: string) =>
    r.includes('Branch Manager') || r.includes('مدير فرع'));

  const getTomorrowDate = () => {
    const d = new Date(); d.setDate(d.getDate() + 1);
    return d.toISOString().split('T')[0];
  };

  // فلاتر
  const [fromDate, setFromDate] = useState(getTomorrowDate());
  const [toDate, setToDate] = useState(getTomorrowDate());
  const [selectedBranches, setSelectedBranches] = useState<number[]>([]);
  const [supplierId, setSupplierId] = useState<number | ''>('');
  const [custodyId, setCustodyId] = useState<number | ''>('');
  const [categoryId, setCategoryId] = useState<number | ''>('');
  const [searchText, setSearchText] = useState('');

  // بحث داخل القوائم
  const [branchSearch, setBranchSearch] = useState('');
  const [supplierSearch, setSupplierSearch] = useState('');
  const [custodySearch, setCustodySearch] = useState('');
  const [categorySearch, setCategorySearch] = useState('');

  // بيانات مرجعية
  const [branches, setBranches] = useState<Branch[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [custodies, setCustodies] = useState<Custody[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);

  const [data, setData] = useState<ConsolidationResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  // فلترة القوائم بالبحث
  const filteredBranches = useMemo(() => {
    if (!branchSearch) return branches;
    const s = branchSearch.toLowerCase();
    return branches.filter(b => b.branchName.toLowerCase().includes(s) || b.branchNameAr.includes(s));
  }, [branches, branchSearch]);

  const filteredSuppliers = useMemo(() => {
    if (!supplierSearch) return suppliers;
    const s = supplierSearch.toLowerCase();
    return suppliers.filter(sp => sp.supplierName.toLowerCase().includes(s) || (sp.supplierNameAr || '').includes(s) || sp.supplierCode.toLowerCase().includes(s));
  }, [suppliers, supplierSearch]);

  const filteredCustodies = useMemo(() => {
    if (!custodySearch) return custodies;
    const s = custodySearch.toLowerCase();
    return custodies.filter(c => c.custodyNumber.toLowerCase().includes(s) || c.employeeName.toLowerCase().includes(s) || (c.employeeNameAr || '').includes(s));
  }, [custodies, custodySearch]);

  const filteredCategories = useMemo(() => {
    if (!categorySearch) return categories;
    const s = categorySearch.toLowerCase();
    return categories.filter(c => c.categoryName.toLowerCase().includes(s) || c.categoryNameAr.includes(s));
  }, [categories, categorySearch]);

  useEffect(() => {
    const fetchLookups = async () => {
      try {
        const [brRes, supRes, cusRes, catRes] = await Promise.all([
          axiosInstance.get('/Branches').catch(() => ({ data: [] })),
          axiosInstance.get('/Suppliers').catch(() => ({ data: [] })),
          axiosInstance.get('/Custodies').catch(() => ({ data: [] })),
          axiosInstance.get('/Categories').catch(() => ({ data: [] })),
        ]);
        setBranches(brRes.data.filter?.((b: any) => b.isActive) || brRes.data);
        setSuppliers(supRes.data);
        setCustodies(cusRes.data.filter?.((c: any) => c.status === 'Open') || cusRes.data);
        setCategories(catRes.data.filter?.((c: any) => c.isActive !== false) || catRes.data);
      } catch { console.error('Failed to fetch lookups'); }
    };
    fetchLookups();
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      let params = `fromDate=${fromDate}&toDate=${toDate}`;
      if (selectedBranches.length > 0) params += `&branchIds=${selectedBranches.join(',')}`;
      if (supplierId) params += `&supplierId=${supplierId}`;
      if (custodyId) params += `&custodyId=${custodyId}`;
      if (categoryId) params += `&categoryId=${categoryId}`;
      if (searchText.trim()) params += `&search=${encodeURIComponent(searchText.trim())}`;
      const response = await axiosInstance.get(`/PurchaseConsolidation/consolidation?${params}`);
      setData(response.data);
      setExpandedRows(new Set());
    } catch (error: any) {
      if (error.response?.status === 403) toast.error(t.noAccess);
      else toast.error(error.response?.data?.message || 'Error');
    } finally { setLoading(false); }
  }, [fromDate, toDate, selectedBranches, supplierId, custodyId, categoryId, searchText]);

  useEffect(() => { if (!isBranchManager) fetchData(); }, []);

  const toggleRow = (key: string) => {
    setExpandedRows(prev => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });
  };

  const handleBranchChange = (event: SelectChangeEvent<number[]>) => {
    const value = event.target.value as number[];
    if (value.includes(-1)) {
      setSelectedBranches(selectedBranches.length === branches.length ? [] : branches.map(b => b.branchId));
    } else { setSelectedBranches(value); }
  };

  const clearFilters = () => {
    setSelectedBranches([]); setSupplierId(''); setCustodyId('');
    setCategoryId(''); setSearchText('');
  };

  // طباعة
  const handlePrint = (detailed: boolean = false) => {
    if (!data || data.items.length === 0) return;
    const pw = window.open('', '_blank'); if (!pw) return;
    const rows = data.items.map(item => {
      let bd = '';
      if (detailed && item.branchDetails.length > 0) {
        bd = `<tr><td colspan="7" style="padding:4px 20px;background:#f9f9f9;"><table width="100%" style="font-size:11px;">
          <tr style="background:#eee;"><th>${t.branch}</th><th>${t.poNumber}</th><th>${t.quantity}</th><th>${t.unitPrice}</th><th>${t.total}</th></tr>
          ${item.branchDetails.map(b => `<tr><td>${isRtl ? b.branchNameAr : b.branchName}</td><td>${b.poNumber}</td><td>${b.quantity.toFixed(2)}</td><td>${b.unitPrice.toFixed(3)}</td><td>${b.totalPrice.toFixed(3)}</td></tr>`).join('')}
        </table></td></tr>`;
      }
      return `<tr><td>${item.itemCode}</td><td>${isRtl ? item.itemNameAr : item.itemName}</td><td>${isRtl ? item.categoryNameAr : item.categoryName}</td><td>${isRtl ? item.unitNameAr : item.unitName}</td><td style="text-align:center;font-weight:bold;">${item.totalQuantity.toFixed(2)}</td><td style="text-align:right;">${item.averageUnitPrice.toFixed(3)}</td><td style="text-align:right;">${item.totalAmount.toFixed(3)}</td></tr>${bd}`;
    }).join('');

    pw.document.write(`<!DOCTYPE html><html dir="${isRtl ? 'rtl' : 'ltr'}"><head><title>${t.title}</title>
      <style>body{font-family:Arial,sans-serif;margin:20px;font-size:12px;}table{width:100%;border-collapse:collapse;margin-top:10px;}th,td{border:1px solid #ccc;padding:6px 8px;text-align:${isRtl ? 'right' : 'left'};}th{background:#333;color:#fff;}.summary{margin:10px 0;font-size:14px;}h2{margin-bottom:5px;}@media print{body{margin:10px;}}</style></head><body>
      <h2>${t.title}</h2><p>${t.fromDate}: <strong>${data.fromDate}</strong> — ${t.toDate}: <strong>${data.toDate}</strong></p>
      ${data.supplierName ? `<p>${t.supplier}: <strong>${data.supplierName}</strong></p>` : ''}
      ${data.custodyName ? `<p>${t.custody}: <strong>${data.custodyName}</strong></p>` : ''}
      <div class="summary">${t.totalOrders}: <strong>${data.totalOrders}</strong> | ${t.totalBranches}: <strong>${data.totalBranches}</strong> | ${t.totalItems}: <strong>${data.totalItems}</strong> | ${t.grandTotal}: <strong>${data.grandTotal.toFixed(3)} ${t.kwd}</strong></div>
      <table><thead><tr><th>${t.itemCode}</th><th>${t.item}</th><th>${t.category}</th><th>${t.unit}</th><th>${t.totalQty}</th><th>${t.avgPrice}</th><th>${t.totalAmount}</th></tr></thead><tbody>${rows}
      <tr style="background:#f0f0f0;font-weight:bold;"><td colspan="6">${t.grandTotal}</td><td style="text-align:right;">${data.grandTotal.toFixed(3)} ${t.kwd}</td></tr></tbody></table></body></html>`);
    pw.document.close(); pw.print();
  };

  if (isBranchManager) {
    return <Box sx={{ p: 3, textAlign: 'center' }}><Typography color="error" variant="h6">{t.noAccess}</Typography></Box>;
  }

  return (
    <Box sx={{ p: 3 }} dir={isRtl ? 'rtl' : 'ltr'}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <ShippingIcon color="primary" sx={{ fontSize: 32 }} />
        <Box>
          <Typography variant="h5" fontWeight="bold">{t.title}</Typography>
          <Typography variant="body2" color="text.secondary">{t.subtitle}</Typography>
        </Box>
      </Box>

      {/* ========== الفلاتر ========== */}
      <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label={t.fromDate}
            value={fromDate ? new Date(fromDate) : null}
            onChange={(date) => {
              const newVal = date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '';
              setFromDate(newVal);
              if (toDate && newVal && toDate < newVal) setToDate('');
            }}
            format="dd/MM/yyyy"
            minDate={minDate}
            maxDate={toDate ? new Date(toDate) : maxDate}
            views={['year', 'month', 'day']}
            openTo="day"
            slotProps={{ textField: { size: 'small', sx: { minWidth: 160 } } }}
          />
        </LocalizationProvider>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label={t.toDate}
            value={toDate ? new Date(toDate) : null}
            onChange={(date) => {
              const newVal = date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '';
              setToDate(newVal);
              if (fromDate && newVal && fromDate > newVal) setFromDate('');
            }}
            format="dd/MM/yyyy"
            minDate={fromDate ? new Date(fromDate) : minDate}
            maxDate={maxDate}
            views={['year', 'month', 'day']}
            openTo="day"
            slotProps={{ textField: { size: 'small', sx: { minWidth: 160 } } }}
          />
        </LocalizationProvider>

        {/* الفروع */}
        <Autocomplete
          multiple
          size="small"
          options={branches}
          getOptionLabel={(o) => isRtl ? (o.branchNameAr || o.branchName) : o.branchName}
          value={branches.filter(b => selectedBranches.includes(b.branchId))}
          onChange={(_, v) => setSelectedBranches(v.map(b => b.branchId))}
          renderInput={(params) => <TextField {...params} label={t.branches} />}
          sx={{ minWidth: 250 }}
          limitTags={2}
        />

        {/* المورد */}
        <Autocomplete
          size="small"
          options={suppliers}
          getOptionLabel={(o) => `${o.supplierCode} - ${isRtl ? (o.supplierNameAr || o.supplierName) : o.supplierName}`}
          value={suppliers.find(s => s.supplierId === supplierId) || null}
          onChange={(_, v) => setSupplierId(v?.supplierId || '')}
          renderInput={(params) => <TextField {...params} label={t.supplier} />}
          sx={{ minWidth: 220 }}
        />

        {/* العهدة */}
        <Autocomplete
          size="small"
          options={custodies}
          getOptionLabel={(o) => `${o.custodyNumber} - ${isRtl ? (o.employeeNameAr || o.employeeName) : o.employeeName}`}
          value={custodies.find(c => c.custodyId === custodyId) || null}
          onChange={(_, v) => setCustodyId(v?.custodyId || '')}
          renderInput={(params) => <TextField {...params} label={t.custody} />}
          sx={{ minWidth: 240 }}
        />

        {/* الفئة */}
        <Autocomplete
          size="small"
          options={categories}
          getOptionLabel={(o) => isRtl ? (o.categoryNameAr || o.categoryName) : o.categoryName}
          value={categories.find(c => c.categoryId === categoryId) || null}
          onChange={(_, v) => setCategoryId(v?.categoryId || '')}
          renderInput={(params) => <TextField {...params} label={t.category} />}
          sx={{ minWidth: 200 }}
        />

        {/* بحث نصي */}
        <TextField size="small" placeholder={t.searchItem} value={searchText} onChange={e => setSearchText(e.target.value)}
          sx={{ minWidth: 160 }} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />

        <Button variant="contained" startIcon={<SearchIcon />} onClick={fetchData} disabled={loading}>{t.search}</Button>
        <Button variant="outlined" startIcon={<ClearIcon />} onClick={clearFilters} size="small">{t.clear}</Button>

        {data && data.items.length > 0 && (<>
          <Button variant="outlined" color="secondary" startIcon={<PrintIcon />} onClick={() => handlePrint(false)}>{t.print}</Button>
          <Button variant="outlined" color="info" startIcon={<PrintIcon />} onClick={() => handlePrint(true)}>{t.printDetailed}</Button>
        </>)}
      </Paper>

      {/* ========== الملخص ========== */}
      {data && data.items.length > 0 && (
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <Chip label={`${t.totalOrders}: ${data.totalOrders}`} color="primary" variant="outlined" />
          <Chip label={`${t.totalBranches}: ${data.totalBranches}`} color="secondary" variant="outlined" />
          <Chip label={`${t.totalItems}: ${data.totalItems}`} color="info" variant="outlined" />
          <Chip label={`${t.grandTotal}: ${data.grandTotal.toFixed(3)} ${t.kwd}`} color="success" />
          {data.supplierName && <Chip label={`${t.supplier}: ${data.supplierName}`} variant="outlined" />}
          {data.custodyName && <Chip label={`${t.custody}: ${data.custodyName}`} variant="outlined" />}
        </Box>
      )}

      {/* ========== الجدول ========== */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress /></Box>
      ) : data && data.items.length > 0 ? (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 'bold', backgroundColor: 'primary.main', color: 'white' } }}>
                <TableCell width={40} /><TableCell>{t.itemCode}</TableCell><TableCell>{t.item}</TableCell>
                <TableCell>{t.category}</TableCell><TableCell>{t.unit}</TableCell>
                <TableCell align="center">{t.totalQty}</TableCell><TableCell align="right">{t.avgPrice}</TableCell>
                <TableCell align="right">{t.totalAmount}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.items.map(item => {
                const key = `${item.itemId}-${item.unitId}`;
                const isExp = expandedRows.has(key);
                return (
                  <React.Fragment key={key}>
                    <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => toggleRow(key)}>
                      <TableCell><IconButton size="small">{isExp ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton></TableCell>
                      <TableCell>{item.itemCode}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>{isRtl ? item.itemNameAr : item.itemName}</TableCell>
                      <TableCell sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>{isRtl ? item.categoryNameAr : item.categoryName}</TableCell>
                      <TableCell>{isRtl ? item.unitNameAr : item.unitName}</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '1.1em', color: 'primary.main' }}>{item.totalQuantity.toFixed(2)}</TableCell>
                      <TableCell align="right">{item.averageUnitPrice.toFixed(3)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>{item.totalAmount.toFixed(3)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={8} sx={{ py: 0, border: 0 }}>
                        <Collapse in={isExp} timeout="auto" unmountOnExit>
                          <Box sx={{ mx: 4, my: 1 }}>
                            <Table size="small">
                              <TableHead><TableRow sx={{ '& th': { fontWeight: 'bold', backgroundColor: 'action.hover', fontSize: '0.8rem' } }}>
                                <TableCell>{t.branch}</TableCell><TableCell>{t.poNumber}</TableCell>
                                <TableCell align="center">{t.quantity}</TableCell><TableCell align="right">{t.unitPrice}</TableCell><TableCell align="right">{t.total}</TableCell>
                              </TableRow></TableHead>
                              <TableBody>
                                {item.branchDetails.map((bd, i) => (
                                  <TableRow key={i}><TableCell>{isRtl ? bd.branchNameAr : bd.branchName}</TableCell><TableCell>{bd.poNumber}</TableCell>
                                    <TableCell align="center">{bd.quantity.toFixed(2)}</TableCell><TableCell align="right">{bd.unitPrice.toFixed(3)}</TableCell><TableCell align="right">{bd.totalPrice.toFixed(3)}</TableCell></TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
              <TableRow sx={{ '& td': { fontWeight: 'bold', backgroundColor: 'action.hover' } }}>
                <TableCell colSpan={7} align={isRtl ? 'left' : 'right'}>{t.grandTotal}</TableCell>
                <TableCell align="right" sx={{ color: 'success.main', fontSize: '1.1em' }}>{data.grandTotal.toFixed(3)} {t.kwd}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      ) : data ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}><Typography color="text.secondary">{t.noData}</Typography></Paper>
      ) : null}
    </Box>
  );
};

export default PurchaseConsolidationPage;